import {
  KdsClipboardButton,
  KdsHeader,
  type KdsHeaderProps,
} from "@kazaar-platform/design-system";
import {
  RiCalendarScheduleLine,
  RiDeleteBinLine,
  RiEditLine,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { OrderAddPurchaseOrderOrderItemReadDtoStatusEnum } from "@/config/apiClient";
import { isPostOrderEditionAvailable } from "@/helpers/validators/isPostOrderEditionAvailable";
import { useToasts } from "@/hooks";
import { useIsOMS } from "@/hooks/useIsOMS";
import {
  isModificationRequestFormInitializedAtom,
  modificationRequestFormAtom,
} from "@/store/atoms/modificationRequestForm";
import { type OrderItemDetailsHeaderProps } from "./OrderItemDetailsHeader.types";

export const OrderItemDetailsHeader = ({
  order,
  orderItem,
  isLoading,
  hasAModificationRequest,
  handleCancelModalOpen,
  isScrolled,
  isRushOrderUIDisplayed,
}: OrderItemDetailsHeaderProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { add } = useToasts();

  const isOMS = useIsOMS();

  const setFormData = useSetAtom(modificationRequestFormAtom);
  const setIsInitialized = useSetAtom(isModificationRequestFormInitializedAtom);

  const excludedStatuses: OrderAddPurchaseOrderOrderItemReadDtoStatusEnum[] = [
    OrderAddPurchaseOrderOrderItemReadDtoStatusEnum.Done,
    OrderAddPurchaseOrderOrderItemReadDtoStatusEnum.Cancelled,
  ];

  const isCancellable = !excludedStatuses.includes(orderItem?.status);

  const isEditionAvailable =
    orderItem &&
    isPostOrderEditionAvailable(orderItem) &&
    !hasAModificationRequest;

  const backUrl = isOMS ? `/oms/orders/${order.id}` : `/orders/${order.id}`;
  const modificationRequestUrl = `/orders/${order.id}/order-items/${orderItem.id}/edit`;

  const editionButton: KdsHeaderProps["buttons"] = [
    {
      buttonType: "icon",
      disabled: !isEditionAvailable,
      icon: RiEditLine,
      color: "neutral" as const,
      variant: "outlined" as const,
      size: "medium" as const,
      onPress: () => {
        setIsInitialized(false);
        setFormData(undefined);
        void navigate(modificationRequestUrl);
      },
    },
  ];

  return (
    <KdsHeader
      isLoading={isLoading}
      backButtonProps={{
        onPress: () => void navigate(backUrl),
      }}
      badgeProps={
        isOMS && isRushOrderUIDisplayed
          ? {
              iconLeft: RiCalendarScheduleLine,
              state: "warning",
              children: t(
                "order.myOrdersItemDetails.rushOrder.badgeLabelRushOrder"
              ),
            }
          : undefined
      }
      buttons={[
        ...(!isOMS ? editionButton : []),
        {
          buttonType: "icon",
          icon: RiDeleteBinLine,
          color: "error",
          variant: "outlined",
          disabled: !isCancellable,
          size: "medium",
          onPress: () => handleCancelModalOpen(true),
        },
      ]}
      isScrolled={isScrolled}
      metadatas={
        order.referenceId
          ? [
              <KdsClipboardButton
                text={order.referenceId}
                key={order.referenceId}
                displayToast={() =>
                  add({
                    key: order.referenceId,
                    state: "reverse",
                    title: t("toast.clipboard.orderId"),
                  })
                }
              />,
            ]
          : []
      }
      title={orderItem.sandboxItem.name}
    />
  );
};
